/**
 * CSS Variable Mapping Utility
 * 
 * Converts theme objects to CSS custom properties for runtime theming.
 */

import type { CheckoutTheme, PartialCheckoutTheme } from "./types.js";

/**
 * CSS variable prefix for checkout theme properties
 */
const CSS_VAR_PREFIX = "--checkout";

/**
 * Convert a camelCase string to kebab-case
 */
function toKebabCase(str: string): string {
  return str.replace(/([A-Z])/g, "-$1").toLowerCase();
}

/**
 * Generate CSS variables from a theme object
 * 
 * @param theme - The complete checkout theme
 * @returns Object with CSS variable names as keys and values
 * 
 * @example
 * ```ts
 * const vars = themeToCssVars(lightTheme);
 * // Returns:
 * // {
 * //   '--checkout-background': '#f9fafb',
 * //   '--checkout-surface': '#ffffff',
 * //   '--checkout-text': '#111827',
 * //   ...
 * // }
 * ```
 */
export function themeToCssVars(
  theme: CheckoutTheme
): Record<string, string> {
  const vars: Record<string, string> = {};
  
  // Colors - set both --checkout-* and --color-checkout-* for Tailwind v4 compatibility
  // Tailwind v4 uses --color-* variables for color utilities
  for (const [key, value] of Object.entries(theme.colors)) {
    const kebabKey = toKebabCase(key);
    vars[`${CSS_VAR_PREFIX}-${kebabKey}`] = value;
    // Also set --color-checkout-* directly for Tailwind v4 color utilities
    vars[`--color-checkout-${kebabKey}`] = value;
  }
  
  // Typography - font families
  vars[`${CSS_VAR_PREFIX}-font-family`] = theme.typography.fontFamily;
  vars[`${CSS_VAR_PREFIX}-font-family-mono`] = theme.typography.fontFamilyMono;
  
  // Typography - font sizes
  for (const [key, value] of Object.entries(theme.typography.fontSize)) {
    vars[`${CSS_VAR_PREFIX}-font-size-${key}`] = value;
  }
  
  // Typography - font weights
  for (const [key, value] of Object.entries(theme.typography.fontWeight)) {
    vars[`${CSS_VAR_PREFIX}-font-weight-${toKebabCase(key)}`] = value;
  }
  
  // Typography - line heights
  for (const [key, value] of Object.entries(theme.typography.lineHeight)) {
    vars[`${CSS_VAR_PREFIX}-line-height-${toKebabCase(key)}`] = value;
  }
  
  // Spacing
  vars[`${CSS_VAR_PREFIX}-radius`] = theme.spacing.radius;
  vars[`${CSS_VAR_PREFIX}-radius-sm`] = theme.spacing.radiusSm;
  vars[`${CSS_VAR_PREFIX}-radius-lg`] = theme.spacing.radiusLg;
  vars[`${CSS_VAR_PREFIX}-radius-full`] = theme.spacing.radiusFull;
  
  return vars;
}

/**
 * Convert CSS variables object to inline style object
 * 
 * @param cssVars - Object with CSS variable names and values
 * @returns React-compatible style object
 */
export function cssVarsToStyle(
  cssVars: Record<string, string>
): React.CSSProperties {
  return cssVars as React.CSSProperties;
}

/**
 * Get CSS variable reference for use in Tailwind arbitrary values
 * 
 * @param varName - The variable name without prefix (e.g., 'background', 'text')
 * @returns CSS var() reference
 * 
 * @example
 * ```ts
 * getCssVar('background') // Returns: 'var(--checkout-background)'
 * ```
 */
export function getCssVar(varName: string): string {
  return `var(${CSS_VAR_PREFIX}-${toKebabCase(varName)})`;
}

/**
 * All CSS variable names used by the checkout theme
 * Useful for documentation and TypeScript autocomplete
 */
export const CSS_VAR_NAMES = {
  // Colors
  background: `${CSS_VAR_PREFIX}-background`,
  surface: `${CSS_VAR_PREFIX}-surface`,
  surfaceHover: `${CSS_VAR_PREFIX}-surface-hover`,
  surfaceActive: `${CSS_VAR_PREFIX}-surface-active`,
  border: `${CSS_VAR_PREFIX}-border`,
  borderSubtle: `${CSS_VAR_PREFIX}-border-subtle`,
  borderFocus: `${CSS_VAR_PREFIX}-border-focus`,
  text: `${CSS_VAR_PREFIX}-text`,
  textMuted: `${CSS_VAR_PREFIX}-text-muted`,
  textSubtle: `${CSS_VAR_PREFIX}-text-subtle`,
  primary: `${CSS_VAR_PREFIX}-primary`,
  primaryForeground: `${CSS_VAR_PREFIX}-primary-foreground`,
  primaryHover: `${CSS_VAR_PREFIX}-primary-hover`,
  primaryMuted: `${CSS_VAR_PREFIX}-primary-muted`,
  success: `${CSS_VAR_PREFIX}-success`,
  successForeground: `${CSS_VAR_PREFIX}-success-foreground`,
  successSubtle: `${CSS_VAR_PREFIX}-success-subtle`,
  successText: `${CSS_VAR_PREFIX}-success-text`,
  successBorder: `${CSS_VAR_PREFIX}-success-border`,
  successMuted: `${CSS_VAR_PREFIX}-success-muted`,
  error: `${CSS_VAR_PREFIX}-error`,
  errorForeground: `${CSS_VAR_PREFIX}-error-foreground`,
  errorSubtle: `${CSS_VAR_PREFIX}-error-subtle`,
  errorText: `${CSS_VAR_PREFIX}-error-text`,
  errorBorder: `${CSS_VAR_PREFIX}-error-border`,
  errorMuted: `${CSS_VAR_PREFIX}-error-muted`,
  warning: `${CSS_VAR_PREFIX}-warning`,
  warningForeground: `${CSS_VAR_PREFIX}-warning-foreground`,
  warningSubtle: `${CSS_VAR_PREFIX}-warning-subtle`,
  warningText: `${CSS_VAR_PREFIX}-warning-text`,
  warningBorder: `${CSS_VAR_PREFIX}-warning-border`,
  warningMuted: `${CSS_VAR_PREFIX}-warning-muted`,
  info: `${CSS_VAR_PREFIX}-info`,
  infoForeground: `${CSS_VAR_PREFIX}-info-foreground`,
  infoSubtle: `${CSS_VAR_PREFIX}-info-subtle`,
  infoText: `${CSS_VAR_PREFIX}-info-text`,
  infoBorder: `${CSS_VAR_PREFIX}-info-border`,
  infoMuted: `${CSS_VAR_PREFIX}-info-muted`,
  inputBorder: `${CSS_VAR_PREFIX}-input-border`,
  inputBackground: `${CSS_VAR_PREFIX}-input-background`,
  inputFocus: `${CSS_VAR_PREFIX}-input-focus`,
  skeleton: `${CSS_VAR_PREFIX}-skeleton`,
  skeletonHighlight: `${CSS_VAR_PREFIX}-skeleton-highlight`,
  
  // Typography
  fontFamily: `${CSS_VAR_PREFIX}-font-family`,
  fontFamilyMono: `${CSS_VAR_PREFIX}-font-family-mono`,
  
  // Spacing
  radius: `${CSS_VAR_PREFIX}-radius`,
  radiusSm: `${CSS_VAR_PREFIX}-radius-sm`,
  radiusLg: `${CSS_VAR_PREFIX}-radius-lg`,
  radiusFull: `${CSS_VAR_PREFIX}-radius-full`,
} as const;


/**
 * The CSS variables for the parts of a theme a caller set, and only those:
 * what CheckoutThemeProvider writes inline. The rest stays with the theme's
 * stylesheet defaults (the components' :host in a web component), so a page
 * that sets the variables on the element from outside (Website v2's
 * bookingComponentCss) is obeyed; a full inline theme beat it.
 */
export function overridesToCssVars(
  overrides: PartialCheckoutTheme | undefined
): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!overrides) return vars;

  for (const [key, value] of Object.entries(overrides.colors ?? {})) {
    if (value === undefined) continue;
    const kebabKey = toKebabCase(key);
    vars[`${CSS_VAR_PREFIX}-${kebabKey}`] = value;
    vars[`--color-checkout-${kebabKey}`] = value;
  }

  const typography = overrides.typography;
  if (typography?.fontFamily) vars[`${CSS_VAR_PREFIX}-font-family`] = typography.fontFamily;
  if (typography?.fontFamilyMono) vars[`${CSS_VAR_PREFIX}-font-family-mono`] = typography.fontFamilyMono;
  for (const [key, value] of Object.entries(typography?.fontSize ?? {})) {
    if (value !== undefined) vars[`${CSS_VAR_PREFIX}-font-size-${key}`] = value;
  }
  for (const [key, value] of Object.entries(typography?.fontWeight ?? {})) {
    if (value !== undefined) vars[`${CSS_VAR_PREFIX}-font-weight-${toKebabCase(key)}`] = value;
  }
  for (const [key, value] of Object.entries(typography?.lineHeight ?? {})) {
    if (value !== undefined) vars[`${CSS_VAR_PREFIX}-line-height-${toKebabCase(key)}`] = value;
  }

  const spacing = overrides.spacing;
  if (spacing?.radius) vars[`${CSS_VAR_PREFIX}-radius`] = spacing.radius;
  if (spacing?.radiusSm) vars[`${CSS_VAR_PREFIX}-radius-sm`] = spacing.radiusSm;
  if (spacing?.radiusLg) vars[`${CSS_VAR_PREFIX}-radius-lg`] = spacing.radiusLg;
  if (spacing?.radiusFull) vars[`${CSS_VAR_PREFIX}-radius-full`] = spacing.radiusFull;

  return vars;
}
